import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { URLHandler } from "src/services/URLHandler.ts";
import { HintBox } from "src/components/HintBox.tsx";
import { ScoreCards } from "src/components/ScoreCards/ScoreCards";
import { useApplicationParameters } from "src/api/client-api/appParams";
import { useProductScoresContext } from "src/context/Product/Scores/useProductScores";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { useProductMetadataContext } from "src/context/Product/Metadata/useProductMetadata";
import { ScoreQueryError } from "src/components/ScoreState/ScoreQueryError";
import { getRemoteErrorStatus } from "src/api/remoteCollectionState";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";

export const OneCompanyScores = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { product, productReference, activePost } = useProductMetadataContext();
    const { company } = useCompanyMetadata();
    const { scores, areScoresRefetching, scoreQueryError, isScoreQueryError, retryScoreQuery } =
        useProductScoresContext();
    const { isFeatureActivated } = useApplicationParameters();
    const isProductUxV2 = useIsProductUxV2();

    const isIntermediateProduct = product.isIntermediate === true;

    // Extraire l'URL de retour depuis les paramètres pour la propager
    const searchParams = new URLSearchParams(location.search);
    const returnUrl = searchParams.get("returnUrl");
    const urlHandlerRoutes = isIntermediateProduct ? URLHandler.intermediateProduct : URLHandler.product;

    const hasSsq = product.ssqId !== undefined;
    const referenceScores = product.jsonAttributes?.["referenceScores"];
    const canDrilldown = isFeatureActivated("FS-product-score-details-distribution");

    if (isProductUxV2 && isScoreQueryError) {
        return (
            <ScoreQueryError
                context="details"
                onRetry={retryScoreQuery}
                status={getRemoteErrorStatus(scoreQueryError)}
            />
        );
    }

    return (
        <>
            {productReference !== null && !hasSsq && referenceScores?.length > 0 && (
                <HintBox title={t("product.nossq.info.title")} content={t("product.nossq.info.body")} />
            )}

            <ScoreCards
                appearance={isProductUxV2 ? "product-v2" : "legacy"}
                scores={scores}
                isLoading={scores === undefined || areScoresRefetching}
                referenceScores={referenceScores}
                prevCampaignName={scores?.prevCampaignName}
                showSpecificityRate={activePost?.isLocked}
                showDetails={canDrilldown}
                showDetailsAction={(scoreId: number) => {
                    const scoreUrl = urlHandlerRoutes.score(company.id, product.id, scoreId);

                    navigate(returnUrl ? `${scoreUrl}?returnUrl=${encodeURIComponent(returnUrl)}` : scoreUrl);
                }}
            />
        </>
    );
};
